import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  CircularProgress,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material"
import ChatIcon from "@mui/icons-material/Chat"
import CloseIcon from "@mui/icons-material/Close"
import SendIcon from "@mui/icons-material/Send"
import "pages/Styles/AiAssistantChat.sass"
import {
  createThread,
  fetchUnreadCount,
  getThread,
  listThreads,
  postMessage,
  SigningAssistantMessage,
} from "./signingAssistantApi"
import type { SigningAssistantFormSnapshot } from "./signingAssistantFormSnapshot"
import { getOrCreateSigningAssistantClientKey } from "./signingAssistantClientSession"
import { useChatScrollToBottomOnAssistant } from "./useChatScrollToBottomOnAssistant"
import { playMessageBeep } from "./playMessageBeep"
import { UI_LAYER_CHAT_DRAWER } from "../constants/uiLayers"
import { renderChatMessageBody } from "./renderChatMessageBody"

type Props = {
  contractId: string
  accessToken: string
  /** Drawer открыт (кнопка — в меню поддержки в App) */
  open: boolean
  onClose: () => void
  /** Для бейджа на основном FAB */
  onUnreadCountChange?: (count: number) => void
  /** Снимок формы подписания (только флаги), подмешивается к сообщению на сервере для ИИ */
  formSnapshot?: SigningAssistantFormSnapshot | null
}

function storageThreadId(contractId: string, clientKey: string): string {
  return `signingAssistant:threadId:${contractId}:${clientKey}`
}

function storageLastSeenSeq(contractId: string, clientKey: string): string {
  return `signingAssistant:lastSeenSeq:${contractId}:${clientKey}`
}

function readLastSeenSeq(contractId: string, clientKey: string): number {
  try {
    const v = sessionStorage.getItem(storageLastSeenSeq(contractId, clientKey))
    if (!v) return 0
    const n = parseInt(v, 10)
    return Number.isFinite(n) ? n : 0
  } catch {
    return 0
  }
}

function writeLastSeenSeq(contractId: string, clientKey: string, seq: number) {
  try {
    sessionStorage.setItem(storageLastSeenSeq(contractId, clientKey), String(seq))
  } catch {
    // ignore
  }
}

function maxAssistantSeq(messages: SigningAssistantMessage[]): number {
  let max = 0
  for (const m of messages) {
    if (m.role === "assistant" && m.seq != null && m.seq > max) {
      max = m.seq
    }
  }
  return max
}

const SigningAssistantChat: React.FC<Props> = ({
  contractId,
  accessToken,
  open,
  onClose,
  onUnreadCountChange,
  formSnapshot = null,
}) => {
  const clientKey = useMemo(() => getOrCreateSigningAssistantClientKey(contractId), [contractId])

  const [threadId, setThreadId] = useState<string | null>(null)
  const [messages, setMessages] = useState<SigningAssistantMessage[]>([])
  const [aiStatus, setAiStatus] = useState<string>("idle")
  const [input, setInput] = useState("")
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [unreadCount, setUnreadCount] = useState(0)
  const prevUnreadRef = useRef<number | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)

  useChatScrollToBottomOnAssistant(open, messages, listRef)

  useEffect(() => {
    if (open) {
      prevUnreadRef.current = unreadCount
      return
    }
    if (prevUnreadRef.current !== null && unreadCount > prevUnreadRef.current) {
      playMessageBeep()
    }
    prevUnreadRef.current = unreadCount
  }, [unreadCount, open])

  const refreshLastSeenFromMessages = useCallback(
    (msgs: SigningAssistantMessage[]) => {
      const m = maxAssistantSeq(msgs)
      if (m > 0) {
        writeLastSeenSeq(contractId, clientKey, m)
      }
    },
    [contractId, clientKey]
  )

  const pollUnreadWhileClosed = useCallback(async () => {
    if (open) return
    const lastSeen = readLastSeenSeq(contractId, clientKey)
    try {
      const n = await fetchUnreadCount(contractId, accessToken, clientKey, lastSeen)
      setUnreadCount(n)
    } catch {
      // ignore
    }
  }, [accessToken, contractId, clientKey, open])

  useEffect(() => {
    onUnreadCountChange?.(unreadCount)
  }, [unreadCount, onUnreadCountChange])

  useEffect(() => {
    void pollUnreadWhileClosed()
    const id = window.setInterval(() => {
      void pollUnreadWhileClosed()
    }, 12000)
    return () => window.clearInterval(id)
  }, [pollUnreadWhileClosed])

  useEffect(() => {
    if (open) {
      void pollUnreadWhileClosed()
    }
  }, [open, pollUnreadWhileClosed])

  useEffect(() => {
    if (!open || !threadId) return
    if (aiStatus !== "processing") return
    const id = window.setInterval(async () => {
      try {
        const data = await getThread(contractId, accessToken, clientKey, threadId)
        setMessages(data.messages || [])
        setAiStatus(data.ai_status || "idle")
        if (data.ai_status !== "processing") {
          refreshLastSeenFromMessages(data.messages || [])
        }
      } catch {
        // ignore
      }
    }, 2500)
    return () => window.clearInterval(id)
  }, [open, threadId, aiStatus, accessToken, contractId, clientKey, refreshLastSeenFromMessages])

  const loadThreadForDrawer = useCallback(async () => {
    const createNewThread = async () => {
      const data = await createThread(contractId, accessToken, clientKey)
      sessionStorage.setItem(storageThreadId(contractId, clientKey), data.thread_id)
      setThreadId(data.thread_id)
      setMessages(data.messages || [])
      setAiStatus(data.ai_status || "idle")
    }

    setError(null)
    setInitialLoading(true)
    try {
      const saved = sessionStorage.getItem(storageThreadId(contractId, clientKey))
      if (saved) {
        try {
          const data = await getThread(contractId, accessToken, clientKey, saved)
          setThreadId(saved)
          setMessages(data.messages || [])
          setAiStatus(data.ai_status || "idle")
          refreshLastSeenFromMessages(data.messages || [])
        } catch {
          sessionStorage.removeItem(storageThreadId(contractId, clientKey))
          const threads = await listThreads(contractId, accessToken, clientKey)
          if (threads.length > 0) {
            const t = threads[0]
            sessionStorage.setItem(storageThreadId(contractId, clientKey), t.thread_id)
            const data = await getThread(contractId, accessToken, clientKey, t.thread_id)
            setThreadId(t.thread_id)
            setMessages(data.messages || [])
            setAiStatus(data.ai_status || "idle")
            refreshLastSeenFromMessages(data.messages || [])
          } else {
            await createNewThread()
          }
        }
      } else {
        const threads = await listThreads(contractId, accessToken, clientKey)
        if (threads.length > 0) {
          const t = threads[0]
          sessionStorage.setItem(storageThreadId(contractId, clientKey), t.thread_id)
          const data = await getThread(contractId, accessToken, clientKey, t.thread_id)
          setThreadId(t.thread_id)
          setMessages(data.messages || [])
          setAiStatus(data.ai_status || "idle")
          refreshLastSeenFromMessages(data.messages || [])
        } else {
          await createNewThread()
        }
      }
      setUnreadCount(0)
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || "Не удалось открыть чат")
    } finally {
      setInitialLoading(false)
    }
  }, [accessToken, contractId, clientKey, refreshLastSeenFromMessages])

  useEffect(() => {
    if (!open) return
    void loadThreadForDrawer()
  }, [open, contractId, accessToken, clientKey, loadThreadForDrawer])

  const handleClose = () => {
    onClose()
    refreshLastSeenFromMessages(messages)
    void pollUnreadWhileClosed()
  }

  const send = async () => {
    const text = input.trim()
    if (!text || !threadId || loading) return
    if (aiStatus === "processing") return
    setInput("")
    setLoading(true)
    setError(null)
    try {
      const data = await postMessage(contractId, accessToken, clientKey, threadId, text, formSnapshot ?? undefined)
      setMessages(data.messages || [])
      setAiStatus(data.ai_status || "idle")
      refreshLastSeenFromMessages(data.messages || [])
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || "Не удалось отправить")
    } finally {
      setLoading(false)
    }
  }

  const showChat = Boolean(threadId) && !initialLoading

  return (
    <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        sx={{ zIndex: UI_LAYER_CHAT_DRAWER }}
        PaperProps={{
          className: "ai-chat__drawer",
          onClick: (e: React.MouseEvent) => e.stopPropagation(),
          style: { display: "flex", flexDirection: "column" },
          /* Только z-index: не задавать position — иначе ломается anchor=right (панель уезжает влево) */
          sx: { zIndex: 1 },
        }}
        ModalProps={{
          disableScrollLock: true,
          sx: { zIndex: UI_LAYER_CHAT_DRAWER },
          BackdropProps: {
            className: "ai-chat__backdrop",
            sx: { zIndex: 0 },
          },
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
          <div className="ai-chat__header">
            <div className="ai-chat__title-group">
              <div className="ai-chat__title">
                <ChatIcon className="ai-chat__title-icon" />
                <Typography variant="h6" component="div">
                  Помощь при подписании
                </Typography>
              </div>
            </div>
            <IconButton onClick={handleClose} size="small" className="ai-chat__close">
              <CloseIcon />
            </IconButton>
          </div>

          <div ref={listRef} className="ai-chat__messages">
            {initialLoading && (
              <div className="ai-chat__loader">
                <CircularProgress size={24} />
              </div>
            )}

            {!initialLoading && showChat && (
              <List dense>
                {messages.length === 0 && (
                  <Typography variant="body2" color="text.secondary" className="ai-chat__empty" style={{ padding: "8px 0" }}>
                    Задайте вопрос по договору или по шагам подписания. Текст договора ассистент подгрузит сам, только
                    если это нужно для ответа.
                  </Typography>
                )}
                {messages.map((msg, idx) => (
                  <ListItem
                    key={`${msg.message_id || idx}`}
                    alignItems="flex-start"
                    className={`ai-chat__message ai-chat__message--${msg.role}`}
                    sx={{ minWidth: 0 }}
                  >
                    <ListItemText
                      primary={msg.role === "assistant" ? "Ассистент" : "Вы"}
                      secondary={renderChatMessageBody(msg.content || "")}
                      secondaryTypographyProps={{ component: "div", sx: { minWidth: 0 } }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </div>

          {error && (
            <Typography color="error" variant="body2" className="ai-chat__error">
              {error}
            </Typography>
          )}

          {showChat && (
            <>
              <div className="ai-chat__input-row">
                <div className="ai-chat__input-wrap">
                  <textarea
                    placeholder="Напишите сообщение…"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    disabled={loading || aiStatus === "processing"}
                    className="ai-chat__textarea"
                    onKeyDown={(e) => {
                      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                        e.preventDefault()
                        void send()
                      }
                    }}
                  />
                </div>
                <span
                  onClick={() => void send()}
                  className="ai-chat__send-wrapper"
                  style={{
                    display: "inline-flex",
                    alignItems: "flex-end",
                    cursor:
                      loading || !input.trim() || aiStatus === "processing" ? "not-allowed" : "pointer",
                    flexShrink: 0,
                  }}
                >
                  <IconButton
                    color="primary"
                    disabled={loading || !input.trim() || aiStatus === "processing"}
                    className="ai-chat__send"
                    sx={{ pointerEvents: "none" }}
                  >
                    {loading ? <CircularProgress size={20} /> : <SendIcon />}
                  </IconButton>
                </span>
              </div>
              {aiStatus === "processing" && (
                <Typography variant="caption" color="text.secondary" style={{ padding: "8px 16px" }}>
                  Ассистент отвечает…
                </Typography>
              )}
            </>
          )}
        </div>
      </Drawer>
  )
}

export default SigningAssistantChat
